import {
  IPurchasable,
  IPurchasablesReducerState,
  getCleanedAndSortedPurchasables,
} from "@smartrr/shared/entities/Purchasable";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { updateMatchInArray } from "@smartrr/shared/utils/updateMatchInArray";
import {
  useDeliveryGroupPurchasables,
  useVariantToPurchasableMap,
} from "@smartrr/shared/utils/useVariantToPurchasableMap";
import flatten from 'lodash/flatten';
import { useMemo } from "react";

import { CustomerSubReducer } from "@customer-app/state/typedCustomerRedux";

import { useSmartrrCustomerSelector } from "../typedCustomerReduxHooks";

const initialState: IPurchasablesReducerState = {
  isLoading: true,
  isLoadingAdditionaly: true,
  isStreaming: false,
  purchasables: [],
  pageNumber: 0,
  pageSize: 0,
  totalCount: 0,
  totalPages: 0,
};

export const purchasablesReducer: CustomerSubReducer<IPurchasablesReducerState> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case "LOADING_PURCHASABLES": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "ERROR_LOADING_PURCHASABLES": {
      return {
        ...state,
        isLoading: false,
      };
    }

    case "LOADED_PURCHASABLES": {
      const purchasables = (action.payload.purchasables || []).data.reduce(
        (purchasablesState, purchasable) =>
          updateMatchInArray(purchasablesState, purchasable, ({ id }) => id, "existingValue"),
        state.purchasables
      );
      return {
        ...state,
        isLoading: false,
        ...action.payload.purchasables,
        // merge because streaming is happening at the same time
        purchasables: getCleanedAndSortedPurchasables(purchasables),
      };
    }

    case "LOADED_ADDITIONAL_PURCHASABLES": {
      const purchasables = (action.payload.purchasables || []).data.reduce(
        (purchasablesState, purchasable) =>
          updateMatchInArray(purchasablesState, purchasable, ({ id }) => id, "existingValue"),
        state.purchasables
      );
      return {
        ...state,
        isLoadingAdditionaly: false,
        ...action.payload.purchasables,
        // merge because streaming is happening at the same time
        purchasables: getCleanedAndSortedPurchasables(purchasables),
      };
    }

    case "LOADED_COMPLETE_PURCHASABLES": {
      const purchasables = (action.payload.purchasables || []).data.reduce(
        (purchasablesState, purchasable) =>
          updateMatchInArray(purchasablesState, purchasable, ({ id }) => id, "existingValue"),
        state.purchasables
      );
      return {
        ...state,
        isLoading: false,
        isLoadingAdditionaly: false,
        ...action.payload.purchasables,
        // merge because streaming is happening at the same time
        purchasables: getCleanedAndSortedPurchasables(purchasables),
      };
    }
    default: {
      return state;
    }
  }
};

export function useCleanedAndSortedPurchables() {
  return useSmartrrCustomerSelector(state => state.purchasables);
}

export function useCustomerPortalVariantToPurchasableMap(): { [id: string]: IPurchasable } {
  return useVariantToPurchasableMap(useCleanedAndSortedPurchables().purchasables);
}

export function useCustomerPortalDeliveryGroupPurchasables(
  scheduledDeliveryGroup: ScheduledDeliveryGroupWithPurchSt[]
): IPurchasable[] {
  return useDeliveryGroupPurchasables(useCleanedAndSortedPurchables().purchasables, scheduledDeliveryGroup);
}

export function useCustomerPortalPurchasableToProductShopifyId(
  smartrrPurchasableId: string | undefined
): string | undefined {
  const { purchasables } = useCleanedAndSortedPurchables();
  return useMemo(
    () => purchasables.find(purchasable => purchasable.id === smartrrPurchasableId)?.shopifyId,
    [smartrrPurchasableId, purchasables]
  );
}

export function useCustomerPortalVariantToProductShopifyId(
  smartrrVariantId: string | null | undefined
): string | undefined {
  const variantToPurchasablesMap = useCustomerPortalVariantToPurchasableMap();
  return smartrrVariantId ? variantToPurchasablesMap[smartrrVariantId]?.shopifyId : undefined;
}

export function useCustomerPortalVariantToVariantShopifyId(
  smartrrVariantId: string | null | undefined
): string | undefined {
  const { purchasables } = useCleanedAndSortedPurchables();
  return useMemo(
    () =>
      flatten(purchasables.map(purch => purch.vnts || [])).find(vnt => vnt.id === smartrrVariantId)?.shopifyId,
    [smartrrVariantId, purchasables]
  );
}
